import { voolistExtension } from './voolist-extension';

/**
 * Keeping the Voolist extension signed in as the same user as the web app.
 *
 * The extension keeps this browser's Voolist login, and renews it on its own, so it can work while
 * Voolist is closed. Left alone it would go on working for whoever was signed in when it last read the
 * login, so the web app tells it every time the signed-in user changes.
 */

/**
 * Whether the signed-in user is now someone else: a login, a logout, or a switch of account. The
 * first answer Firebase gives after the page loads only says who was already signed in, so it is not
 * a change — the extension reads the page's login on every page load anyway.
 *
 * @param {string|null|undefined} previousUid - undefined before Firebase's first answer; null when
 *   nobody was signed in
 * @param {string|null} uid - null when nobody is signed in now
 * @returns {boolean}
 */
export function isSignedInUserChange (previousUid, uid) {
  return previousUid !== undefined && previousUid !== uid;
}

/**
 * Tell the extension every time the signed-in user changes.
 *
 * @param {function(function(Object|null): void): function(): void} subscribe - Firebase's
 *   `onAuthStateChanged`, bound to the app's auth: it calls the listener with the signed-in user, or
 *   null, and returns the way to stop
 * @returns {function(): void} Stops telling the extension
 */
export function tellExtensionWhenSignedInUserChanges (subscribe) {
  let previousUid;
  return subscribe((user) => {
    const uid = user?.uid ?? null;
    if (isSignedInUserChange(previousUid, uid)) voolistExtension.notifyAuthChanged();
    previousUid = uid;
  });
}
